// AuraShowcase.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Aura is the LWD unfair advantage on the discovery side, same way The Edit
// is the unfair advantage on the editorial side. This section makes the
// case: vendors apply to LWD because Aura surfaces them to couples whose
// brief actually fits, not to a generic search audience.
//
// Visual: always-dark, mirroring The Edit Showcase's dark beat. The two
// sections together form LWD's "unfair advantage spread", discovery on
// the left, story on the right.
//
// The sample exchange is stylised, not a verbatim transcript. Tagged
// "illustrative" so the page stays truthful.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { AURA_SHOWCASE } from '../data/inclusionContent.js';

const GD   = 'var(--font-heading-primary)';
const FS   = 'var(--font-serif-body, Georgia, "Times New Roman", serif)';
const NU   = 'var(--font-body)';
const GOLD = '#c9a96e';

const BG       = '#070604';
const SURFACE  = '#0e0c09';
const SURFACE2 = '#13110c';
const TEXT     = '#f0ebe0';
const BODY     = 'rgba(240,235,224,0.78)';
const SUBTLE   = 'rgba(240,235,224,0.42)';
const BORDER   = 'rgba(240,235,224,0.10)';

export default function AuraShowcase() {
  const [orbActive, setOrbActive] = useState(false);
  // Subtle pulse on the gold orb to suggest "intelligence, alive."
  useEffect(() => {
    const t = setInterval(() => setOrbActive(v => !v), 3200);
    return () => clearInterval(t);
  }, []);

  function handleMeetAura() {
    if (typeof window === 'undefined') return;
    if (AURA_SHOWCASE.ctaEvent) {
      // Aura's launcher listens for this event globally; if it isn't
      // mounted on the page yet, the dispatch is harmless.
      window.dispatchEvent(new Event(AURA_SHOWCASE.ctaEvent));
    }
  }

  return (
    <section style={{
      background:   BG,
      padding:      'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
      color:        TEXT,
      borderTop:    `1px solid ${BORDER}`,
      borderBottom: `1px solid ${BORDER}`,
      position:     'relative',
      overflow:     'hidden',
    }}>
      {/* Soft golden ambient wash so the section reads as "intelligent",
          not flat black. Sits behind everything, low opacity. */}
      <div aria-hidden style={{
        position: 'absolute',
        top:    '50%',
        left:   '50%',
        transform: 'translate(-50%, -50%)',
        width:  'min(820px, 90vw)',
        height: 'min(820px, 90vw)',
        background: 'radial-gradient(circle at center, rgba(201,168,110,0.07) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 1080, margin: '0 auto', position: 'relative' }}>
        {/* Eyebrow + title */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(48px, 6vw, 80px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 24 }}>
            <span style={{ width: 36, height: 1, background: GOLD }} />
            <span style={{
              fontFamily: NU, fontSize: 9, fontWeight: 600,
              letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD,
            }}>
              {AURA_SHOWCASE.eyebrow}
            </span>
            <span style={{ width: 36, height: 1, background: GOLD }} />
          </div>

          {/* Aura orb, gentle pulsing gold ring */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 56, height: 56, borderRadius: '50%',
            background: 'radial-gradient(circle at 30% 30%, rgba(201,168,110,0.55), rgba(201,168,110,0.10) 65%, transparent 75%)',
            boxShadow: orbActive
              ? '0 0 0 0 rgba(201,168,110,0.0), 0 0 32px rgba(201,168,110,0.45)'
              : '0 0 0 0 rgba(201,168,110,0.0), 0 0 14px rgba(201,168,110,0.20)',
            transition: 'box-shadow 1.6s ease',
            marginBottom: 28,
            position: 'relative',
          }}>
            <span aria-hidden style={{
              position: 'absolute', inset: '14px',
              borderRadius: '50%',
              border: `1px solid ${GOLD}aa`,
            }} />
            <span aria-hidden style={{
              width: 6, height: 6, borderRadius: '50%',
              background: GOLD,
              boxShadow: `0 0 12px ${GOLD}`,
            }} />
          </div>

          <h2 style={{
            fontFamily: GD,
            fontSize:   'clamp(32px, 4.5vw, 56px)',
            fontWeight: 400,
            color:      TEXT,
            lineHeight: 1.08,
            letterSpacing: '-0.01em',
            margin:     '0 auto 36px',
            maxWidth:   780,
          }}>
            {AURA_SHOWCASE.title}
          </h2>

          {/* Three sharp statements, short, declarative, weighted. The
              brand promise of Aura distilled to three lines before the
              explanatory body. Bumped in size and spacing so the lines
              read as the section's centre of gravity, not as a subtitle.
              These are the assertive clarity beats, they should land
              with weight, not whisper past. */}
          {AURA_SHOWCASE.pillars?.length > 0 && (
            <div style={{
              display: 'flex', flexDirection: 'column',
              gap: 'clamp(14px, 1.6vw, 22px)',
              alignItems: 'center',
              maxWidth: 760, margin: '0 auto 36px',
            }}>
              {AURA_SHOWCASE.pillars.map((line, i) => (
                <p key={i} style={{
                  fontFamily: GD,
                  fontSize:   'clamp(20px, 2.2vw, 30px)',
                  fontStyle:  'italic',
                  fontWeight: 400,
                  color:      TEXT,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.35,
                  margin:     0,
                }}>
                  {line}
                </p>
              ))}
            </div>
          )}

          <p style={{
            fontFamily: NU, fontSize: 'clamp(14px, 1.4vw, 17px)',
            color: BODY, lineHeight: 1.7, margin: '0 auto', maxWidth: 640,
          }}>
            {AURA_SHOWCASE.body}
          </p>
        </div>

        {/* Sample exchange, couple brief + Aura's response */}
        <div style={{
          maxWidth: 720, margin: '0 auto',
          display: 'flex', flexDirection: 'column', gap: 18,
          marginBottom: 'clamp(40px, 5vw, 64px)',
        }}>
          <div style={{
            fontFamily: NU, fontSize: 8, fontWeight: 600,
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: SUBTLE, textAlign: 'center', marginBottom: 4,
          }}>
            Illustrative · How Aura responds
          </div>

          {/* Couple's brief */}
          <Bubble
            speaker="Couple"
            speakerColor={SUBTLE}
            background="transparent"
            align="left"
            text={AURA_SHOWCASE.sample.couple}
            border={`1px solid ${BORDER}`}
            italic
          />

          {/* Aura's response, gold-edged */}
          <Bubble
            speaker="Aura"
            speakerColor={GOLD}
            background={SURFACE2}
            align="right"
            text={AURA_SHOWCASE.sample.aura}
            border={`1px solid ${GOLD}33`}
          />
        </div>

        {/* Mechanism bullets, brief, declarative */}
        {AURA_SHOWCASE.bullets?.length > 0 && (
          <div style={{
            maxWidth: 720, margin: '0 auto',
            background: SURFACE,
            border: `1px solid ${BORDER}`,
            padding: 'clamp(24px, 3vw, 40px) clamp(24px, 3vw, 40px)',
          }}>
            <div style={{
              fontFamily: NU, fontSize: 8, fontWeight: 600,
              letterSpacing: '0.32em', textTransform: 'uppercase',
              color: GOLD, marginBottom: 18,
            }}>
              How it works
            </div>
            <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {AURA_SHOWCASE.bullets.map((b, i) => (
                <li key={i} style={{
                  display: 'flex', gap: 16, alignItems: 'flex-start',
                  padding: '10px 0',
                  borderTop: i === 0 ? 'none' : `1px solid ${BORDER}`,
                  fontFamily: NU, fontSize: 14, lineHeight: 1.6,
                  color: BODY,
                }}>
                  <span aria-hidden style={{
                    width: 4, height: 4, marginTop: 9, flexShrink: 0,
                    background: GOLD, transform: 'rotate(45deg)',
                  }} />
                  <span>{b}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* CTA */}
        <div style={{ textAlign: 'center', marginTop: 'clamp(40px, 5vw, 64px)' }}>
          <button
            onClick={handleMeetAura}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              fontFamily: NU, fontSize: 11, fontWeight: 500,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: GOLD,
              background: 'none',
              border: 'none',
              outline: 'none',
              cursor: 'pointer',
              padding: 0,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {AURA_SHOWCASE.cta}
            <span aria-hidden style={{
              width: 5, height: 5, background: GOLD, transform: 'rotate(45deg)',
            }} />
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Helper: speech bubble ──────────────────────────────────────────────────
function Bubble({ speaker, speakerColor, background, border, align, text, italic }) {
  return (
    <div style={{
      maxWidth: '85%',
      alignSelf: align === 'right' ? 'flex-end' : 'flex-start',
      background,
      border,
      padding: 'clamp(18px, 2vw, 24px) clamp(20px, 2.4vw, 28px)',
      borderRadius: 2,
    }}>
      <div style={{
        fontFamily: NU, fontSize: 9, fontWeight: 600,
        letterSpacing: '0.28em', textTransform: 'uppercase',
        color: speakerColor, marginBottom: 8,
      }}>
        {speaker}
      </div>
      <div style={{
        fontFamily: italic ? FS : NU,
        fontStyle: italic ? 'italic' : 'normal',
        fontSize: 'clamp(14px, 1.3vw, 16px)',
        lineHeight: 1.65,
        color: TEXT,
      }}>
        {text}
      </div>
    </div>
  );
}
